import React from "react";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { useTranslation } from "react-i18next";
import {
  getClassLabelFromAkeneoObject,
  getSecondaryClassLabelFromAkeneoObject,
} from "../utils/akeneoUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import styled from "styled-components";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { useMaterials } from "../hooks/useMaterials";
import { isInteger } from "../utils/rowUtils";
import {
  MaterialFormDataWithSpecificCodes,
  CupsSecondaryMaterials,
} from "../types/materialForm.types";
import { InfoDot } from "./infoDot";
import { Button } from "primereact/button";
import { InputInstructionsHover } from "./inputInstructionsHover";

interface EmptyCupTabProps {
  ecomodClasses: EcomodClass[];
  control: Control<MaterialFormDataWithSpecificCodes, any>;
  watch?: any;
  allEcomodClasses: EcomodClass[];
}

const EmptyCupTabContainer = styled.div`
  padding: 1rem;

  & h3 {
    margin-top: 0;
    margin-bottom: 2rem;
    font-weight: 500;
    font-size: 1.2rem;
  }

  & strong {
    font-weight: 500;
  }

  & .p-inputtext,
  & .p-inputnumber-input {
    min-width: 150px;
    font-size: 0.8rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr 1fr;
  gap: 0 1rem;
  width: 100%;
  max-width: 800px;
  margin-bottom: 2rem;
`;

const Header = styled.div<{ $span?: number; $strong?: boolean }>`
  font-weight: ${(props) => (props.$strong ? "500" : "400")};
  grid-column: span ${(props) => props.$span || 1};
  white-space: nowrap;
  font-size: 0.9rem;
  min-height: 36px;
  display: flex;
  align-items: center;
`;

const LabelCell = styled.div`
  font-size: 0.8rem;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
  padding-right: 0.5rem;
  max-width: 300px;
`;

const InputCell = styled.div<{ $gray?: boolean }>`
  font-size: 0.8rem;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${(props) => (props.$gray ? "#f2f2f2" : "transparent")};
  border-radius: var(--border-radius);
  padding: 0.5rem;
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
  margin: 2rem 0;
`;

const SectionContainer = styled.div`
  position: relative;
`;

const SecondaryMatButton = styled.a`
  font-size: 0.8rem;
  text-align: right;
  text-decoration: underline;
  cursor: pointer;
  display: block;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
`;

const RemoveButton = styled(Button)`
  font-size: 0.8rem;
  padding: 0.2rem 0;
  width: 15px;
  margin-left: 5px;
`;

const EmptyCupTab: React.FC<EmptyCupTabProps> = ({
  ecomodClasses,
  control,
  watch,
  allEcomodClasses,
}) => {
  const { t } = useTranslation();
  const [locale, toast] = useUIStore(
    useShallow((state: UIState) => [state.locale, state.toastRef]),
  );
  const multiMaterialInstructionKey =
    "input-instructions.multi-material-main-material-only";

  const filteredOutMainEcomodClasses = ["1", "2", "3", "4", "5", "6"];
  const plasticBags = ["2A2", "2A3", "2D2", "2D3"];
  const { multiMaterialPackageCodes } = useMaterials();

  // Use useFieldArray for managing secondary materials arrays
  const consumerSecondaryFields = useFieldArray({
    control,
    name: "consumerCups.secondaryMaterials",
  });

  const businessSecondaryFields = useFieldArray({
    control,
    name: "businessCups.secondaryMaterials",
  });

  // Filter materials for consumer cups - split into single and multi
  const allConsumerCupsSingle = ecomodClasses.filter(
    (cls) =>
      !cls.nameFi?.toLowerCase().includes("teollisuuden") &&
      !filteredOutMainEcomodClasses.includes(cls.code || "") &&
      !plasticBags.includes(cls.code || "") &&
      !multiMaterialPackageCodes.includes(cls.code || ""),
  );

  const allConsumerCupsMulti = ecomodClasses.filter(
    (cls) =>
      !cls.nameFi?.toLowerCase().includes("teollisuuden") &&
      !filteredOutMainEcomodClasses.includes(cls.code || "") &&
      !plasticBags.includes(cls.code || "") &&
      multiMaterialPackageCodes.includes(cls.code || ""),
  );

  // Filter materials for business cups - split into single and multi
  const allBusinessCupsSingle = ecomodClasses.filter(
    (cls) =>
      !filteredOutMainEcomodClasses.includes(cls.code || "") &&
      !plasticBags.includes(cls.code || "") &&
      !cls.nameFi?.toLowerCase().includes("teollisuuden") &&
      !multiMaterialPackageCodes.includes(cls.code || ""),
  );

  const allBusinessCupsMulti = ecomodClasses.filter(
    (cls) =>
      !filteredOutMainEcomodClasses.includes(cls.code || "") &&
      !plasticBags.includes(cls.code || "") &&
      !cls.nameFi?.toLowerCase().includes("teollisuuden") &&
      multiMaterialPackageCodes.includes(cls.code || ""),
  );

  // Get available secondary materials
  const availableSecondaryMaterials = allEcomodClasses
    .filter((cls) => isInteger(cls.code as string))
    .map((cls) => ({
      label: getSecondaryClassLabelFromAkeneoObject(locale, cls),
      value: cls.code,
      cls: cls,
    }));

  // Handler for adding secondary material
  const handleAddSecondaryMaterial = (
    packageType: "consumer" | "business",
    ecomodClassCode: string,
  ) => {
    const hasValues = watch
      ? (() => {
          const producedInFinland = watch(
            `${packageType}Cups.${multiMaterialPackageCodes.includes(ecomodClassCode) ? "multiMaterial." : ""}producedInFinland.${ecomodClassCode}`,
          );
          const importedCups = watch(
            `${packageType}Cups.${multiMaterialPackageCodes.includes(ecomodClassCode) ? "multiMaterial." : ""}importedCups.${ecomodClassCode}`,
          );

          return producedInFinland || importedCups;
        })()
      : false;

    if (!hasValues) {
      toast?.current?.show({
        severity: "warn",
        detail: t("add_secondary_material_first"),
        life: 3000,
      });
      return;
    }

    // Get available ecomod materials (basic materials with integer codes)
    const availableEcomodMaterials = allEcomodClasses?.filter((cls) =>
      isInteger(cls.code as string),
    );

    if (!availableEcomodMaterials || availableEcomodMaterials.length === 0) {
      console.warn("No available ecomod materials found");
      return;
    }

    // Initialize with the first available material as default
    const defaultMaterial = availableEcomodMaterials[0];

    const newSecondaryMaterial: CupsSecondaryMaterials = {
      materialCode: defaultMaterial.code || "",
      ecoModulationClassCode: ecomodClassCode,
      producedInFinland: "",
      importedCups: "",
    };

    // Use the proper useFieldArray append method
    if (packageType === "consumer") {
      consumerSecondaryFields.append(newSecondaryMaterial);
    } else {
      businessSecondaryFields.append(newSecondaryMaterial);
    }
  };

  const handleRemoveSecondaryMaterial = (
    packageType: "consumer" | "business",
    index: number,
  ) => {
    if (packageType === "consumer") {
      consumerSecondaryFields.remove(index);
    } else {
      businessSecondaryFields.remove(index);
    }
  };

  // Helper function to get secondary materials for a specific ecomod class
  const getSecondaryMaterialsForEcomod = (
    packageType: "consumer" | "business",
    ecomodClassCode: string,
  ) => {
    const secondaryMaterials =
      packageType === "consumer"
        ? consumerSecondaryFields.fields
        : businessSecondaryFields.fields;

    if (!secondaryMaterials) return [];

    return secondaryMaterials.filter(
      (secMat) => secMat.ecoModulationClassCode === ecomodClassCode,
    );
  };

  // Render single material cups grid with two columns
  const renderSingleCupsGrid = (
    packageType: "consumer" | "business",
    materials: EcomodClass[],
  ) => {
    if (materials.length === 0) return null;

    return (
      <Grid>
        {/* Headers */}
        <Header $strong={true}>
          <InfoDot
            position="right"
            text={t("infotext-single-materials")}
            content={
              <div>
                <p>{t("infotext-single-materials-part-1")}</p>
                <p>{t("infotext-single-materials-part-2")}</p>
                <p>{t("infotext-single-materials-part-3")}</p>
              </div>
            }
            marginLeft="10px"
          >
            {t("label-single-materials")}
          </InfoDot>
        </Header>
        <Header>{t("header-cups-produced-in-finland")}</Header>
        <Header>{t("header-imported-cups")}</Header>

        {/* Material rows */}
        {materials.map((cls) => (
          <React.Fragment key={cls.code}>
            {/* Label */}
            <LabelCell>
              <span>{getClassLabelFromAkeneoObject(locale, cls)}</span>
            </LabelCell>

            {/* Cups Produced in Finland */}
            <InputCell $gray={true}>
              <Controller
                name={`${packageType}Cups.producedInFinland.${cls.code}`}
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    onChange={(e) => field.onChange(e.value)}
                    value={parseFloat(field.value) || undefined}
                    suffix=" kg"
                    useGrouping={false}
                    locale="fi-FI"
                  />
                )}
              />
            </InputCell>

            {/* Imported Cups */}
            <InputCell $gray={false}>
              <Controller
                name={`${packageType}Cups.importedCups.${cls.code}`}
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    onChange={(e) => field.onChange(e.value)}
                    value={parseFloat(field.value) || undefined}
                    suffix=" kg"
                    useGrouping={false}
                    locale="fi-FI"
                  />
                )}
              />
            </InputCell>
          </React.Fragment>
        ))}
      </Grid>
    );
  };

  // Render multi material cups grid
  const renderMultiCupsGrid = (
    packageType: "consumer" | "business",
    materials: EcomodClass[],
  ) => {
    if (materials.length === 0) return null;

    return (
      <Grid>
        {/* Headers */}
        <Header $strong={true}>
          <InfoDot
            position="right"
            text=""
            content={
              <div>
                <p>{t("infotext-multi-materials-part-1")}</p>
                <p>{t("infotext-multi-materials-part-2")}</p>
                <p>{t("infotext-multi-materials-part-3")}</p>
                <p>{t("infotext-multi-materials-part-4")}</p>
              </div>
            }
            marginLeft="10px"
          >
            {t("label-multi-materials")}
          </InfoDot>
        </Header>
        <Header>{t("header-cups-produced-in-finland")}</Header>
        <Header>{t("header-imported-cups")}</Header>

        {/* Material rows */}
        {materials.map((cls) => {
          const secondaryMats = getSecondaryMaterialsForEcomod(
            packageType,
            cls.code as string,
          );

          return (
            <React.Fragment key={cls.code}>
              {/* Main material row */}
              <LabelCell>
                <span>{getClassLabelFromAkeneoObject(locale, cls)}</span>
              </LabelCell>

              {/* Cups Produced in Finland */}
              <InputCell $gray={true}>
                <Controller
                  name={`${packageType}Cups.multiMaterial.producedInFinland.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={multiMaterialInstructionKey}
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Imported Cups */}
              <InputCell $gray={false}>
                <Controller
                  name={`${packageType}Cups.multiMaterial.importedCups.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={multiMaterialInstructionKey}
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Secondary material rows */}
              {secondaryMats.map((secMat, secIndex) => {
                const actualIndex =
                  (packageType === "consumer"
                    ? consumerSecondaryFields.fields
                    : businessSecondaryFields.fields
                  )?.findIndex((sm) => sm === secMat) ?? -1;

                return (
                  <React.Fragment key={secMat.id}>
                    {/* Secondary material label (dropdown) */}
                    <LabelCell>
                      <Controller
                        name={
                          `${packageType}Cups.secondaryMaterials[${actualIndex}].materialCode` as any
                        }
                        control={control}
                        render={({ field }) => (
                          <div style={{ display: "flex" }}>
                            <Dropdown
                              options={availableSecondaryMaterials}
                              placeholder={t("placeholder-select-material")}
                              value={field.value}
                              onChange={(e) => {
                                field.onChange(e.value);
                              }}
                              style={{
                                minWidth: "300px",
                                maxWidth: "300px",
                              }}
                            />
                            <RemoveButton
                              icon="pi pi-times"
                              text
                              size="small"
                              type="button"
                              onClick={() =>
                                handleRemoveSecondaryMaterial(
                                  packageType,
                                  actualIndex,
                                )
                              }
                            ></RemoveButton>
                          </div>
                        )}
                      />
                    </LabelCell>

                    {/* Cups Produced in Finland */}
                    <InputCell $gray={true}>
                      <Controller
                        name={
                          `${packageType}Cups.secondaryMaterials[${actualIndex}].producedInFinland` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>

                    {/* Imported Cups */}
                    <InputCell $gray={false}>
                      <Controller
                        name={
                          `${packageType}Cups.secondaryMaterials[${actualIndex}].importedCups` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>
                  </React.Fragment>
                );
              })}

              {/* Add secondary material button row */}
              <LabelCell>
                <SecondaryMatButton
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleAddSecondaryMaterial(packageType, cls.code as string)
                  }
                >
                  {t("cmd-add-secondary-material")}
                </SecondaryMatButton>
              </LabelCell>
              <div />
              <div />
            </React.Fragment>
          );
        })}
      </Grid>
    );
  };

  return (
    <EmptyCupTabContainer className="empty-cup-tab">
      <span style={{ display: "flex", marginBottom: "0.5rem" }}>
        {t("label-empty-cup-info")}
      </span>
      {/* Consumer Cups Section */}
      <SectionContainer>
        <h3>
          <InfoDot
            position="right"
            text={t("infotext-consumer-packaging")}
            marginLeft="10px"
          >
            {t("header-consumer-packaging")}
          </InfoDot>
        </h3>
        {renderSingleCupsGrid("consumer", allConsumerCupsSingle)}
        {renderMultiCupsGrid("consumer", allConsumerCupsMulti)}
      </SectionContainer>

      <SectionDivider />

      {/* Business Cups Section */}
      <SectionContainer>
        <h3>
          <InfoDot
            position="right"
            text={t("infotext-business-packaging")}
            marginLeft="10px"
          >
            {t("header-business-packaging")}
          </InfoDot>
        </h3>
        {renderSingleCupsGrid("business", allBusinessCupsSingle)}
        {renderMultiCupsGrid("business", allBusinessCupsMulti)}
      </SectionContainer>
    </EmptyCupTabContainer>
  );
};

export default EmptyCupTab;
